import React, { useState } from "react";
import { Box, Typography, TextField, Tabs, Tab } from "@mui/material";
import {
  BrandBatchSettings,
  type BrandInstructions,
  defaultSettings,
} from "../../../pages/playground/types";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type BrandInstructionType =
  | "brand_context"
  | "brand_rules"
  | "brand_values"
  | "brand_tone_of_voice";

type BrandInstructionConfig = {
  key: BrandInstructionType;
  label: string;
  description: string;
};

const brandInstructionConfigs: BrandInstructionConfig[] = [
  {
    key: "brand_context",
    label: "Brand Context",
    description: "Brand history and positioning",
  },
  {
    key: "brand_rules",
    label: "Brand Rules",
    description: "Content guidelines and rules",
  },
  {
    key: "brand_values",
    label: "Brand Values",
    description: "Core principles and values",
  },
  {
    key: "brand_tone_of_voice",
    label: "Tone of Voice",
    description: "Communication style and tone",
  },
];

type BrandInstructionsProps = {
  settings: BrandBatchSettings;
  validationErrors?: Record<string, string>;
  onChange: (key: string, newSettings: BrandBatchSettings) => void;
};

const BrandInstructions: React.FC<BrandInstructionsProps> = ({
  settings,
  validationErrors = {},
  onChange,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const brandInstructions =
    settings.brand_instructions || defaultSettings.brand_instructions!;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBrandInstructionChange = (
    field: keyof BrandInstructions,
    value: string
  ) => {
    const newBrandInstructions = {
      ...brandInstructions,
      [field]: value,
    };

    const newSettings = {
      ...settings,
      brand_instructions: newBrandInstructions,
    };

    onChange(`brand_instructions.${field}`, newSettings);
  };

  const hasErrors = Object.keys(validationErrors).some((key) =>
    key.startsWith("brand_instructions.")
  );

  const currentConfig = brandInstructionConfigs[activeTab];
  const currentKey = currentConfig.key;
  const currentValue = brandInstructions?.[currentKey] || "";

  return (
    <CollapsibleFormElement title="Brand Instructions" hasError={hasErrors}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Configure the AI prompt instructions specific to this brand. These
          settings will guide the AI in generating content that aligns with your
          brand's voice and requirements.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {brandInstructionConfigs.map((config) => (
          <Tab
            key={config.key}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {config.label}
              </Box>
            }
            sx={{ textTransform: "none" }}
          />
        ))}
      </Tabs>

      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">{currentConfig.label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {currentConfig.description}
          </Typography>
        </Box>

        <TextField
          multiline
          rows={6}
          fullWidth
          placeholder={`Enter ${currentConfig.label.toLowerCase()} instructions...`}
          value={currentValue}
          onChange={(e) =>
            handleBrandInstructionChange(currentKey, e.target.value)
          }
          error={!!validationErrors[`brand_instructions.${currentKey}`]}
          helperText={
            validationErrors[`brand_instructions.${currentKey}`] || undefined
          }
          variant="outlined"
          size="small"
        />
      </Box>
    </CollapsibleFormElement>
  );
};

export default BrandInstructions;
